(() => {
  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const heroBeats = ['MATERIAL RECEIVED', 'CUT / ASSEMBLY', 'QUALITY SEALED', 'HARVEST CLEARED'];
  const inspectionBeats = ['SEALED', 'SEAL RELEASED', 'WRAP OPEN', 'PRODUCT VERIFIED'];
  const phaseNames = { harvest: '01 / Harvest', shipment: '02 / Shipment', arrival: '03 / Arrival', distribution: '04 / Distribution' };

  const money = (cents, currency = 'USD') => {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100); }
    catch (_) { return `$${(cents / 100).toFixed(2)}`; }
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;' })[character]);

  function initOperation() {
    const root = q('[data-wwb-operation]');
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    const gate = q('[data-entry-gate]', root);
    const heroTrack = q('[data-hero-track]', root);
    const heroVideo = q('[data-hero-video]', root);
    const packageTrack = q('[data-package-track]', root);
    const packageStage = q('[data-package-stage]', root);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let entered = !gate;
    let animationFrame = 0;
    let inspectionBeat = -1;

    if (gate) document.body.classList.add('intro-open');

    q('[data-enter-world]', root)?.addEventListener('click', () => {
      entered = true;
      gate.classList.add('entry-gate--open');
      document.body.classList.remove('intro-open');
      if (!reduceMotion && heroVideo) {
        heroVideo.currentTime = 0;
        heroVideo.play().catch(() => undefined);
      }
      window.setTimeout(() => q('#MainContent')?.focus(), 650);
    });

    const updateScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      doc.style.setProperty('--page-progress', max > 0 ? String(window.scrollY / max) : '0');

      if (heroTrack) {
        const bounds = heroTrack.getBoundingClientRect();
        const travel = Math.max(1, heroTrack.offsetHeight - window.innerHeight);
        const progress = Math.min(1, Math.max(0, -bounds.top / travel));
        const exit = Math.min(1, Math.max(0, (progress - 0.74) / 0.26));
        const titleFade = Math.max(0, 1 - progress / 0.68);
        heroTrack.style.setProperty('--hero-progress', progress.toFixed(4));
        heroTrack.style.setProperty('--hero-route', `${progress * 100}%`);
        heroTrack.style.setProperty('--hero-bg-scale', `${1.045 + progress * 0.3}`);
        heroTrack.style.setProperty('--hero-bg-x', `${-progress * 9}vw`);
        heroTrack.style.setProperty('--hero-bg-y', `${progress * 3.5}vh`);
        heroTrack.style.setProperty('--hero-scan-x', `${-18 + progress * 136}%`);
        heroTrack.style.setProperty('--hero-scan-opacity', `${Math.min(1, progress * 6) * Math.max(0, 1 - exit)}`);
        heroTrack.style.setProperty('--hero-frame-inset', `${window.innerWidth < 560 ? 1.1 + progress * 0.7 : 2.2 + progress * 2.4}rem`);
        heroTrack.style.setProperty('--hero-frame-opacity', `${0.18 + progress * 0.42}`);
        heroTrack.style.setProperty('--hero-copy-y', `${-45 - progress * 42}%`);
        heroTrack.style.setProperty('--hero-title-opacity', titleFade.toFixed(3));
        heroTrack.style.setProperty('--hero-world-x', `${-progress * 12}vw`);
        heroTrack.style.setProperty('--hero-wide-x', `${progress * 7}vw`);
        heroTrack.style.setProperty('--hero-bricks-x', `${-progress * 4}vw`);
        heroTrack.style.setProperty('--hero-stamp-turn', `${8 + progress * 210}deg`);
        heroTrack.style.setProperty('--hero-stamp-x', `${progress * -7}vw`);
        heroTrack.style.setProperty('--hero-stamp-y', `${progress * 18}vh`);
        heroTrack.style.setProperty('--hero-exit-y', `${(1 - exit) * 100}%`);
        heroTrack.style.setProperty('--hero-exit-opacity', `${exit}`);
        heroTrack.style.setProperty('--hero-cue-opacity', `${Math.max(0, 1 - progress * 7)}`);
        const beat = Math.min(3, Math.floor(progress * 4));
        heroTrack.dataset.beat = String(beat);
        const beatLabel = q('[data-hero-beat]', heroTrack);
        const beatIndex = q('[data-hero-index]', heroTrack);
        if (beatLabel) beatLabel.textContent = heroBeats[beat];
        if (beatIndex) beatIndex.textContent = `0${beat + 1} / 04`;
        qa('.harvest-stations span', heroTrack).forEach((station, index) => station.classList.toggle('is-active', Math.min(beat, 2) === index));
        if (heroVideo) heroVideo.playbackRate = 0.72 + progress * 0.63;
      }

      if (packageTrack && packageStage) {
        const bounds = packageTrack.getBoundingClientRect();
        const travel = Math.max(1, packageTrack.offsetHeight - window.innerHeight);
        const routeProgress = Math.min(1, Math.max(0, -bounds.top / travel));
        const progress = reduceMotion ? 1 : Math.min(1, routeProgress / 0.82);
        const beat = Math.min(3, Math.floor(progress * 4));
        const frame = Math.min(47, Math.round(progress * 47));
        const width = packageStage.clientWidth;
        const height = packageStage.clientHeight;
        const frameWidth = Math.max(width, height * (4 / 3));
        const frameHeight = frameWidth / (4 / 3);
        const column = frame % 8;
        const row = Math.floor(frame / 8);
        packageTrack.style.setProperty('--package-progress', progress.toFixed(4));
        packageTrack.style.setProperty('--package-scan', `${progress * 100}%`);
        packageTrack.style.setProperty('--package-sheet-width', `${frameWidth * 8}px`);
        packageTrack.style.setProperty('--package-sheet-height', `${frameHeight * 6}px`);
        packageTrack.style.setProperty('--package-frame-x', `${-(column * frameWidth + (frameWidth - width) / 2)}px`);
        packageTrack.style.setProperty('--package-frame-y', `${-(row * frameHeight + (frameHeight - height) / 2)}px`);
        if (beat !== inspectionBeat) {
          inspectionBeat = beat;
          packageTrack.dataset.inspection = String(beat);
          const label = q('[data-inspection-label]', packageTrack);
          const index = q('[data-inspection-index]', packageTrack);
          if (label) label.textContent = inspectionBeats[beat];
          if (index) index.textContent = `0${beat + 1}`;
          qa('.inspection-steps li', packageTrack).forEach((step, stepIndex) => {
            step.classList.toggle('is-active', stepIndex === beat);
            step.classList.toggle('is-passed', stepIndex < beat);
          });
        }
      }

      const chapters = qa('[data-chapter]', root);
      const anchor = window.innerHeight * 0.35;
      let active = chapters.find((chapter) => {
        const bounds = chapter.getBoundingClientRect();
        return bounds.top <= anchor && bounds.bottom > anchor;
      });
      if (!active && chapters.length) active = chapters.reduce((closest, chapter) => Math.abs(chapter.getBoundingClientRect().top - anchor) < Math.abs(closest.getBoundingClientRect().top - anchor) ? chapter : closest);
      if (active) {
        const phase = active.dataset.chapter;
        qa('[data-phase-link]', root).forEach((link) => link.classList.toggle('is-active', link.dataset.phaseLink === phase));
        const current = q('[data-current-phase]', root);
        if (current) current.textContent = phaseNames[phase];
      }
      animationFrame = 0;
    };

    const scheduleScroll = () => { if (!animationFrame) animationFrame = requestAnimationFrame(updateScroll); };
    window.addEventListener('scroll', scheduleScroll, { passive: true });
    window.addEventListener('resize', scheduleScroll, { passive: true });
    window.addEventListener('pointermove', (event) => {
      const x = event.clientX / window.innerWidth;
      const y = event.clientY / window.innerHeight;
      document.documentElement.style.setProperty('--mx', `${x * 100}%`);
      document.documentElement.style.setProperty('--my', `${y * 100}%`);
      document.documentElement.style.setProperty('--shift-x', `${(x - 0.5) * 18}px`);
      document.documentElement.style.setProperty('--shift-y', `${(y - 0.5) * 12}px`);
    }, { passive: true });

    qa('[data-story-toggle]', root).forEach((button) => button.addEventListener('click', () => {
      const target = q(`#${button.dataset.storyToggle}-chapter-one`, root);
      if (!target) return;
      const opening = target.hidden;
      target.hidden = !opening;
      button.classList.toggle('is-open', opening);
      button.setAttribute('aria-expanded', String(opening));
      q('i', button).textContent = opening ? '−' : '+';
      const label = button.firstElementChild?.firstChild;
      if (label?.nodeType === Node.TEXT_NODE) label.textContent = `${opening ? 'Close' : 'Read'} Chapter One`;
      if (opening) observeNarratives(target);
    }));

    const openManifest = () => {
      q('.site-menu', root)?.classList.add('site-menu--open');
      q('.manifest-backdrop', root)?.classList.add('manifest-backdrop--open');
      qa('[data-manifest-toggle]', root).forEach((button) => { button.classList.add('is-open'); button.setAttribute('aria-expanded', 'true'); });
      q('.site-menu', root)?.setAttribute('aria-hidden', 'false');
    };
    const closeManifest = () => {
      q('.site-menu', root)?.classList.remove('site-menu--open');
      q('.manifest-backdrop', root)?.classList.remove('manifest-backdrop--open');
      qa('[data-manifest-toggle]', root).forEach((button) => { button.classList.remove('is-open'); button.setAttribute('aria-expanded', 'false'); });
      q('.site-menu', root)?.setAttribute('aria-hidden', 'true');
    };
    qa('[data-manifest-toggle]', root).forEach((button) => button.addEventListener('click', () => q('.site-menu', root)?.classList.contains('site-menu--open') ? closeManifest() : openManifest()));
    qa('[data-manifest-close]', root).forEach((button) => button.addEventListener('click', closeManifest));
    qa('.site-menu a', root).forEach((link) => link.addEventListener('click', closeManifest));
    q('[data-replay]', root)?.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      if (gate) window.setTimeout(() => { entered = false; gate.classList.remove('entry-gate--open'); document.body.classList.add('intro-open'); }, reduceMotion ? 0 : 350);
    });

    if (!reduceMotion) {
      const videoObserver = new IntersectionObserver((entries) => entries.forEach((entry) => {
        const video = entry.target;
        if (!entered) return;
        if (entry.isIntersecting) video.play().catch(() => undefined); else video.pause();
      }), { rootMargin: '-15% 0px -15%', threshold: 0.12 });
      qa('[data-chapter-video]', root).forEach((video) => videoObserver.observe(video));
    }
    updateScroll();
  }

  function observeNarratives(root = document) {
    const paragraphs = qa('[data-narrative-paragraph]:not(.is-visible)', root);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return paragraphs.forEach((paragraph) => paragraph.classList.add('is-visible'));
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.18 });
    paragraphs.forEach((paragraph) => observer.observe(paragraph));
  }

  function initProductGalleries(root = document) {
    qa('[data-product-gallery]', root).forEach((gallery) => {
      if (gallery.dataset.galleryReady === 'true') return;
      const slides = qa('[data-gallery-slide]', gallery);
      const thumbnails = qa('[data-gallery-thumb]', gallery);
      const stage = q('[data-gallery-stage]', gallery);
      const count = q('[data-gallery-count]', gallery);
      if (!slides.length) return;
      gallery.dataset.galleryReady = 'true';
      let activeIndex = Math.max(0, slides.findIndex((slide) => !slide.hidden));
      let touchStartX = null;

      const activate = (requestedIndex, shouldFocus = false) => {
        const index = (requestedIndex + slides.length) % slides.length;
        activeIndex = index;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
          if (!active) qa('video', slide).forEach((video) => video.pause());
        });
        thumbnails.forEach((thumbnail, thumbnailIndex) => {
          const active = thumbnailIndex === index;
          thumbnail.classList.toggle('is-active', active);
          thumbnail.setAttribute('aria-current', String(active));
        });
        thumbnails[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        if (count) count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
        if (shouldFocus) stage?.focus({ preventScroll: true });
      };

      thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener('click', () => activate(index)));
      q('[data-gallery-previous]', gallery)?.addEventListener('click', () => activate(activeIndex - 1, true));
      q('[data-gallery-next]', gallery)?.addEventListener('click', () => activate(activeIndex + 1, true));
      stage?.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        activate(activeIndex + (event.key === 'ArrowLeft' ? -1 : 1));
      });
      stage?.addEventListener('touchstart', (event) => { touchStartX = event.changedTouches[0]?.clientX ?? null; }, { passive: true });
      stage?.addEventListener('touchend', (event) => {
        if (touchStartX === null) return;
        const delta = (event.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
        touchStartX = null;
        if (Math.abs(delta) < 45) return;
        activate(activeIndex + (delta < 0 ? 1 : -1));
      }, { passive: true });
      activate(activeIndex);
    });
  }

  let currentCart = null;
  const drawer = () => q('[data-cargo-drawer]');

  function openCargo() {
    drawer()?.classList.add('is-open');
    q('.cargo-backdrop')?.classList.add('is-open');
    drawer()?.setAttribute('aria-hidden', 'false');
    qa('[data-cargo-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    document.body.classList.add('cargo-open');
    refreshCart();
  }

  function closeCargo() {
    drawer()?.classList.remove('is-open');
    q('.cargo-backdrop')?.classList.remove('is-open');
    drawer()?.setAttribute('aria-hidden', 'true');
    qa('[data-cargo-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('cargo-open');
  }

  async function refreshCart() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      currentCart = await response.json();
      renderCart(currentCart);
    } catch (_) { /* Preserve server-rendered cart when a refresh fails. */ }
  }

  function renderCart(cart) {
    qa('[data-cart-count]').forEach((count) => { count.textContent = String(cart.item_count || 0).padStart(2, '0'); });
    const lines = q('[data-cart-lines]');
    const summary = q('[data-cart-summary]');
    if (!lines || !summary) return;
    if (!cart.items?.length) {
      lines.innerHTML = `<div class="cargo-empty"><p>NO ITEMS ASSIGNED</p><strong>The route is waiting.</strong><a href="/collections">Explore collections <span>↗</span></a></div>`;
      summary.hidden = true;
      return;
    }
    lines.innerHTML = cart.items.map((item) => `<div class="cargo-line" data-cart-line="${escapeHtml(item.key)}">${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" width="90" height="116">` : ''}<div class="cargo-line-copy"><span>${escapeHtml(item.vendor || 'WWB')}</span><strong>${escapeHtml(item.product_title)}</strong><small>${escapeHtml(item.variant_title || '')}</small><div class="cargo-quantity"><button type="button" data-cart-change="${escapeHtml(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button><span>${item.quantity}</span><button type="button" data-cart-change="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button></div></div><span class="cargo-line-price">${money(item.final_line_price, cart.currency)}</span></div>`).join('');
    q('[data-cart-total]').textContent = money(cart.total_price, cart.currency);
    summary.hidden = false;
  }

  document.addEventListener('click', async (event) => {
    const openButton = event.target.closest('[data-cargo-open]');
    if (openButton) { event.preventDefault(); openCargo(); return; }
    const closeButton = event.target.closest('[data-cargo-close]');
    if (closeButton) { event.preventDefault(); closeCargo(); return; }
    const changeButton = event.target.closest('[data-cart-change]');
    if (!changeButton) return;
    changeButton.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: changeButton.dataset.cartChange, quantity: Number(changeButton.dataset.quantity) }) });
      renderCart(await response.json());
    } finally { changeButton.disabled = false; }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form], .wwb-product-form');
    if (!form) return;
    event.preventDefault();
    const submit = q('[type="submit"]', form);
    if (submit) submit.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) throw await response.json();
      await refreshCart();
      openCargo();
    } catch (error) {
      const message = error?.description || 'This garment could not be added to the route.';
      const status = q('[data-product-status]', form);
      if (status) status.textContent = message;
    } finally { if (submit) submit.disabled = false; }
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const button = q('[type="submit"]', select.form);
    if (!button) return;
    button.disabled = option.dataset.available !== 'true';
    q('[data-product-price]')?.replaceChildren(document.createTextNode(option.dataset.price || ''));
    q('[data-product-button-label]')?.replaceChildren(document.createTextNode(button.disabled ? 'Route closed' : 'Add to cargo'));
    if (option.dataset.mediaId) {
      qa('[data-gallery-thumb]').find((thumbnail) => thumbnail.dataset.mediaId === option.dataset.mediaId)?.click();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeCargo();
    q('[data-manifest-close]')?.click();
  });

  document.addEventListener('DOMContentLoaded', () => { initOperation(); initProductGalleries(); refreshCart(); });
  document.addEventListener('shopify:section:load', (event) => { initOperation(); initProductGalleries(event.target); });
})();
